var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TableBody } from '../../common';
import { QueueType } from '../../multiskill/types';
import { PLAN_COLORS } from '../../wizard/constants';
import { MainRow, QueueRow } from '../common/periodSectionBody';
import { PeriodSectionTable } from '../common/periodSectionTable';
import { getLobExpansionKey, getMultiskillExpansionKeys, getMultiskillGroupExpansionKeys, getMultiskillSectionExpansionKey, getQueueExpansionKeysForLob } from '../common/queueRow';
import { INDENT_LEVELS } from '../common/utils';
var SUPPORT_STAFF_TOTAL_KEY = 'totalSupportStaff';
var SUPPORT_STAFF_KEY = 'supportStaff';
var SupportStaffSectionBody = function (_a) {
  var _b, _c;
  var data = _a.data,
    addColumns = _a.addColumns,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var t = useTranslation('report', {
    keyPrefix: 'period.reportRows'
  }).t;
  var roles = React.useMemo(function () {
    var _a;
    return Object.keys((_a = data === null || data === void 0 ? void 0 : data.supportStaff) !== null && _a !== void 0 ? _a : {});
  }, [data]);
  var isMultiskill = data.dataType === QueueType.Multiskill;
  var rowColor = (_b = PLAN_COLORS[data === null || data === void 0 ? void 0 : data.colorIndex]) === null || _b === void 0 ? void 0 : _b[1];
  var isIncludeTotal = addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Total');
  var isTotalExpanded = (_c = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[SUPPORT_STAFF_TOTAL_KEY]) !== null && _c !== void 0 ? _c : false;
  var hasRoles = roles.length > 0;
  return _jsxs(TableBody, {
    children: [_jsx(MainRow, {
      bold: true,
      data: data.totalSupportStaff,
      rowColor: rowColor,
      title: t('totalSupportStaff'),
      isHideRow: true,
      isIncludeTotal: isIncludeTotal,
      averagePrecision: 1,
      showDetails: isTotalExpanded,
      onShowRow: hasRoles ? function () {
        return onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(SUPPORT_STAFF_TOTAL_KEY);
      } : undefined,
      isMultiskill: isMultiskill
    }), isTotalExpanded && roles.map(function (role) {
      var _a;
      var isRoleExpanded = (_a = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[role]) !== null && _a !== void 0 ? _a : false;
      return _jsxs(React.Fragment, {
        children: [_jsx(MainRow, {
          bold: true,
          data: data.supportStaff[role],
          title: role,
          rowColor: rowColor,
          summaryColor: rowColor,
          averagePrecision: 1,
          isIncludeTotal: isIncludeTotal,
          isMultiskill: isMultiskill,
          isHideRow: true,
          showDetails: isRoleExpanded,
          onShowRow: function () {
            return onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(role);
          },
          nestLevel: INDENT_LEVELS.lob
        }), isRoleExpanded && _jsx(QueueRow, {
          data: data.queues,
          dataWithLob: data.queuesByLob,
          isIncludeTotal: isIncludeTotal,
          keyItem: SUPPORT_STAFF_KEY,
          rowColor: rowColor,
          role: role,
          averagePrecision: 1,
          isMultiskill: isMultiskill,
          lobIndent: INDENT_LEVELS.queue,
          queueIndent: INDENT_LEVELS.country,
          countryIndent: INDENT_LEVELS.location,
          locationIndent: INDENT_LEVELS.supportStaffLocation,
          expandedItems: expandedItems,
          onToggleItem: onToggleItem
        })]
      }, role);
    })]
  });
};
export var SupportStaffTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var roles = React.useMemo(function () {
    var _a;
    return Object.keys((_a = data === null || data === void 0 ? void 0 : data.supportStaff) !== null && _a !== void 0 ? _a : {});
  }, [data]);
  var lobs = React.useMemo(function () {
    var _a;
    return (_a = data.queuesByLob) !== null && _a !== void 0 ? _a : [];
  }, [data.queuesByLob]);
  var expansionItems = React.useMemo(function () {
    return __spreadArray(__spreadArray([SUPPORT_STAFF_TOTAL_KEY], roles, true), roles.flatMap(function (role) {
      return lobs.flatMap(function (lob) {
        return __spreadArray([getLobExpansionKey(lob.lobName, SUPPORT_STAFF_KEY, role)], getQueueExpansionKeysForLob(lob, SUPPORT_STAFF_KEY, role), true);
      }).concat(getMultiskillExpansionKeys(data.queues, SUPPORT_STAFF_KEY, role));
    }), true);
  }, [data.queues, lobs, roles]);
  var getNestedExpansionItems = React.useCallback(function (itemName) {
    var getRoleNestedItems = function (role) {
      return lobs.flatMap(function (lob) {
        return __spreadArray([getLobExpansionKey(lob.lobName, SUPPORT_STAFF_KEY, role)], getQueueExpansionKeysForLob(lob, SUPPORT_STAFF_KEY, role), true);
      }).concat(getMultiskillExpansionKeys(data.queues, SUPPORT_STAFF_KEY, role));
    };
    if (itemName === SUPPORT_STAFF_TOTAL_KEY) {
      return __spreadArray(__spreadArray([], roles, true), roles.flatMap(getRoleNestedItems), true);
    }
    if (roles.includes(itemName)) {
      return getRoleNestedItems(itemName);
    }
    var multiskillRole = roles.find(function (roleName) {
      return itemName === getMultiskillSectionExpansionKey(SUPPORT_STAFF_KEY, roleName);
    });
    if (multiskillRole) return getMultiskillGroupExpansionKeys(data.queues, SUPPORT_STAFF_KEY, multiskillRole);
    var role = roles.find(function (roleName) {
      return itemName.startsWith("".concat(SUPPORT_STAFF_KEY, "::").concat(roleName, "::"));
    });
    if (!role) return [];
    var lobEntry = lobs.find(function (lob) {
      return itemName === getLobExpansionKey(lob.lobName, SUPPORT_STAFF_KEY, role);
    });
    if (!lobEntry) return [];
    return getQueueExpansionKeysForLob(lobEntry, SUPPORT_STAFF_KEY, role);
  }, [data.queues, lobs, roles]);
  return _jsx(PeriodSectionTable, {
    title: t('reportRows.supportStaff'),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: expansionItems,
    getNestedExpansionItems: getNestedExpansionItems,
    defaultExpandedItems: [SUPPORT_STAFF_TOTAL_KEY],
    tableBodyComponent: SupportStaffSectionBody
  });
};